import {
  Box,
  BoxProps,
  IconButton,
  styled,
  Typography,
  TypographyProps,
  useTheme,
} from "@mui/material";
// import { OverridableComponent } from "@mui/material/OverridableComponent";
import { motion } from "motion/react";
import { useCallback, useEffect, useState } from "react";
import { usePrevious } from "react-use";
import Loading from "Components/Loading/Loading";
import { HTMLMotionProps } from "motion/react";
import { TFramerAnimationProps } from "Interfaces";
import LightbulbIcon from "Components/icons/LightbulbIcon";
import { ReactNode } from "react";
import AnalyticsStatCardIcon from "features/analytics/components/AnalyticsStatCardIcon";
import { useIsMobile } from "Hooks/useIsMobile";

const StyledBulbButton = styled(IconButton)(({ theme }) => ({
  color: "rgba(255,255,255, 0.7)",
  position: "absolute",
  top: 14,
  right: 18,
  width: 25,
  height: 25,
  fill: "white",
  "&:hover": {
    color: "white",
    background: "transparent",
  },
}));

export interface IStatCardProps extends BoxProps {
  direction?: "up" | "down";
  minHeight?: string | number;
  loading?: boolean;
  bgIndex?: number;
  noBg?: boolean;
  fullHeight?: boolean;
  onDescribe?: () => void;
  icon?: ReactNode;
}

interface IStyledStatCardProps
  extends BoxProps, Pick<HTMLMotionProps<any>, TFramerAnimationProps> {
  minHeight?: string | number;
  bgIndex?: number;
  noBg?: boolean;
  fullHeight?: boolean;
  loading?: boolean;
}
const StyledStatCardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "fullHeight",
})<{ fullHeight?: boolean }>(({ fullHeight }) => ({
  flex: 1,
  height: fullHeight ? "100%" : undefined,
}));

const BaseStyledStatCard = ({
  px = 2,
  py = 1.5,
  ...rest
}: IStyledStatCardProps) => {
  return <Box px={px} py={py} {...rest} />;
};

const StyledStatCard = styled(BaseStyledStatCard, {
  shouldForwardProp: (prop) =>
    prop !== "bgIndex" &&
    prop !== "fullHeight" &&
    prop !== "noBg" &&
    prop !== "minHeight",
})<IStyledStatCardProps>(({
  minHeight = 117,
  bgIndex,
  noBg,
  fullHeight,
  loading,
  theme,
}) => {
  const bgColor =
    bgIndex === 1
      ? "#6b64ff"
      : bgIndex === 2
        ? "#8251db"
        : bgIndex === 3
          ? "#994ce8"
          : "#b84be5";

  return {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: loading ? "center" : "flex-start",
    minHeight,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    color: "white",
    backgroundColor: "white",
    background: !noBg ? bgColor : undefined,
    backgroundSize: "cover",
    height: fullHeight ? "100%" : undefined,
  };
});

export const StatCard = ({
  direction,
  children,
  loading,
  bgIndex: initialBgIndex,
  noBg,
  fullHeight,
  onDescribe,
  icon,
  ...rest
}: IStatCardProps) => {
  const isMobile = useIsMobile();
  const [bgIndex, setBgIndex] = useState(initialBgIndex || 1);
  const [loaded, setLoaded] = useState(false);
  const prevLoading = usePrevious(loading);
  const [statRef, setStatRef] = useState<HTMLDivElement | null>(null);
  const onRefChange = useCallback((node) => {
    setStatRef(node);
  }, []);

  useEffect(() => {
    if (prevLoading && !loading) {
      setLoaded(true);
    }
  }, [loading, prevLoading]);

  useEffect(() => {
    const parent = statRef?.parentNode?.parentNode?.parentNode;
    if (!parent) return; // Exit early if parent is not available

    const statCards = parent.querySelectorAll(".stat-card");

    statCards.forEach((card, index) => {
      if (card === statRef) {
        setBgIndex((index % 4) + 1);
      }
    });
  }, [statRef]);

  return (
    <StyledStatCardWrapper
      ref={onRefChange}
      className="stat-card"
      sx={{ flex: 1 }}
      fullHeight={fullHeight}
    >
      <StyledStatCard
        loading={loading || !loaded}
        bgIndex={initialBgIndex || bgIndex}
        noBg={noBg}
        component={motion.div}
        fullHeight={fullHeight}
        initial={{
          opacity: 0,
          scale: 0.7,
        }}
        animate={{
          opacity: 1,
          scale: 1,
        }}
        {...rest}
      >
        {loading || !loaded ? (
          <Loading />
        ) : (
          <>
            <>{children}</>
            {onDescribe && !isMobile && (
              <StyledBulbButton onClick={onDescribe}>
                {icon ? (
                  <AnalyticsStatCardIcon icon={icon} noBg={noBg} />
                ) : (
                  <LightbulbIcon />
                )}
              </StyledBulbButton>
            )}
          </>
        )}
      </StyledStatCard>
    </StyledStatCardWrapper>
  );
};

interface IStatCardRoundProps extends IStatCardProps {
  size?: "small" | "medium" | number;
  backgroundColor?: number;
}

const BaseStatCardRound = ({ p = 1.5, ...rest }: IStatCardRoundProps) => {
  return <Box p={p} {...rest} />;
};

export const StatCardRound = styled(BaseStatCardRound, {
  shouldForwardProp: (prop) => prop !== "lines",
})<IStatCardRoundProps>(({ size, theme, direction, backgroundColor }) => ({
  position: "relative",
  height: typeof size === "number" ? size : size === "medium" ? 207 : 180,
  width: typeof size === "number" ? size : size === "medium" ? 207 : 180,
  borderRadius: "50%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.charts[backgroundColor || 0],
  "&::before": {
    content: "''",
    position: "absolute",
    top: -10,
    bottom: -10,
    left: -10,
    right: -10,
    border: "3px solid",
    borderRadius: "50%",
    opacity: 0.3,
    borderColor: theme.palette.charts[backgroundColor || 0],
  },
}));

export const StatCardCenterContainer = styled(Box)(() => ({
  minHeight: "100%",
  justifyContent: "center",
  display: "flex",
  flexDirection: "column",
}));

type TSize = "xs" | "small" | "medium" | "large";

interface IStatCardTypographyProps extends TypographyProps {
  size?: TSize;
  fontWeight?: number;
  directionColour?: "up" | "down";
  dense?: boolean;
  lines?: number;
}

const StyledStatCardTypography = styled(Typography)<IStatCardTypographyProps>(
  ({ theme, size, dense, lines, display, color }) => ({
    lineHeight: dense ? 1 : 1.25,
    color: !color
      ? `rgba(255,255,255,${size === "xs" || size === "small" ? 0.8 : 1})`
      : undefined,
    overflow: lines ? "hidden" : undefined,
    textOverflow: "ellipsis",
    display: display ? undefined : "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    textTransform:
      size === "large" || size === "medium" ? "capitalize" : "initial",
  }),
);

export const StatCardTypography = ({
  children,
  ...props
}: IStatCardTypographyProps) => {
  const theme = useTheme();
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const onRefChange = useCallback((node) => {
    setContainerRef(node);
  }, []);

  const [fontSize, setFontSize] = useState(
    props.size === "xs" || props.size === "small" ? 16 : 26,
  );
  const text = children as string;

  useEffect(() => {
    if (!containerRef || props.fontSize) {
      return;
    }
    const adjustFontSize = () => {
      const containerWidth = containerRef?.offsetWidth;
      if (containerWidth == null) return; // Exit early if containerWidth is null or undefined

      let textWidth = getTextWidth(
        text,
        `${props.size === "xs" || props.size === "small" ? 16 : 26}px ${
          theme.typography.fontFamily
        }`,
      );
      const padding = 0;

      if (textWidth + padding > containerWidth) {
        // see if 15 - 24 would fit
        let textWidth = getTextWidth(
          text,
          `${props.size === "xs" || props.size === "small" ? 15 : 24}px ${
            theme.typography.fontFamily
          }`,
        );
        if (textWidth + padding > containerWidth) {
          setFontSize(props.size === "xs" || props.size === "small" ? 14 : 22);
        } else {
          setFontSize(props.size === "xs" || props.size === "small" ? 15 : 24);
        }
      } else {
        setFontSize(props.size === "xs" || props.size === "small" ? 16 : 26);
      }
    };

    adjustFontSize();

    // Add event listener for window resize
    window.addEventListener("resize", adjustFontSize);
    return () => {
      // Cleanup: remove event listener
      window.removeEventListener("resize", adjustFontSize);
    };
  }, [
    text,
    props.size,
    theme.typography.fontFamily,
    containerRef,
    props.fontSize,
  ]);

  // Function to measure text width
  const getTextWidth = (text: string, font: string) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return 0; // Return 0 if context is not available
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  };

  return (
    <span ref={onRefChange}>
      <StyledStatCardTypography
        {...props}
        fontSize={fontSize}
        fontWeight={props.fontWeight ? props.fontWeight : 600}
      >
        {children}
      </StyledStatCardTypography>
    </span>
  );
};

const BaseStatCardRoundTypography = ({
  align = "center",
  ...rest
}: IStatCardTypographyProps) => {
  // get rif of small and use xs
  if (rest.size === "small") {
    rest.size = "xs";
  }
  return <Typography align={align} {...rest} />;
};

export const StatCardRoundTypography = styled(BaseStatCardRoundTypography)(
  ({ theme, size, directionColour, dense, lines }) => ({
    fontSize: size === "small" ? 14 : size === "medium" ? 18 : 24,
    fontWeight: size === "small" ? 600 : "bold",
    lineHeight: dense ? 1 : 1.2,
    paddingTop: dense ? 2.5 : 0,
    paddingBottom: dense ? 2.5 : 0,
    color:
      directionColour === "up"
        ? "#DDFF80"
        : directionColour === "down"
          ? "#FFCED9"
          : `rgba(255,255,255,${size === "xs" || size === "small" ? 0.8 : 1})`, //todo, handle direction colour in context of background
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
  }),
);
